import { BellOutlined, CloseOutlined } from '@ant-design/icons';
import { useAuthContext } from '@context/auth/useAuthContext';
import useNotifications from '@context/notifications/useNotificationContext';
import { useWebPush } from '@context/web-push';
import { Button } from 'antd';
import { JSX, memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useInvalidateResetQueries from '../notifications/hooks/useInvalidateResetQueries';

type PermissionsAction = {
  id: string;
  label: string | JSX.Element;
  action: () => void;
};

const WebPushPermissions = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const { openNotification } = useNotifications();
  // const navigate = useNavigate();
  const { enabled, showPrompt, lastMessage, enable, remindLater, closePrompt } = useWebPush();

  const {
    refetchWidgetNotificationsAsync,
    refetchFullNotificationPageAsync,
    resetInfiniteQueriesCacheToNotFetchAllTheOffsetPages,
  } = useInvalidateResetQueries({
    userId: user.userId,
  });

  const refetchNotifications = async () => {
    await refetchWidgetNotificationsAsync();
    await refetchFullNotificationPageAsync();
  };

  const PermissionsActions: PermissionsAction[] = [
    {
      id: 'enable',
      label: t('web-push-enable'),
      action: async () => {
        await enable();
      },
    },
    {
      id: 'remind-me',
      label: t('web-push-remind-later'),
      action: () => remindLater(),
    },
    {
      id: 'close',
      label: <CloseOutlined />,
      action: () => closePrompt(),
    },
  ];

  // Show notification when a new message arrives
  useEffect(() => {
    if (!lastMessage) return;

    // First reset to not fetch all the offset pages then invalidate
    resetInfiniteQueriesCacheToNotFetchAllTheOffsetPages();

    // Delay the refetch slightly so the backend has time to persist the notification
    // before we query for it. Without this the list comes back empty of the new entry.
    setTimeout(() => {
      refetchNotifications();
    }, 3000);

    // Info, not Success. An arriving notification is not the outcome of anything
    // the reader just did - a new note or a schedule change is news, and a green
    // tick over it reads as "your action worked".
    openNotification('topRight', {
      title: lastMessage.title ?? 'N/A',
      description: lastMessage.body ?? 'N/A',
      type: 'Info',
      // onClick: () => {
      //   console.log('lastMessage', lastMessage);
      //   if (
      //     lastMessage.title === 'Service Request Data Export Completed' ||
      //     lastMessage.title === 'Visit Data Export Completed'
      //   ) {
      //     closeNotification();
      //     return;
      //   }

      //   navigate(`dashboard/notifications/list?id=${lastMessage.notificationId}`);
      //   closeNotification();
      // },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage]);

  // Don't render if notifications are enabled or prompt shouldn't be shown
  if (enabled || !showPrompt) return null;

  return (
    <div
      className="flex w-full justify-between p-4 bg-[#3e63dd] text-white text-[0.8rem]"
      style={{ background: '#3e63dd', color: 'white', fontSize: '0.8rem' }}
    >
      <div className="flex justify-start items-center gap-2">
        <div>
          <BellOutlined />
        </div>
        <div>{t('web-push-permission-prompt')}</div>
      </div>

      <div className="flex justify-end items-center gap-4">
        {PermissionsActions.map((action) => (
          <Button className="bg-transparent text-white" key={action.id} onClick={action.action} variant="outlined">
            {action.label}
          </Button>
        ))}
      </div>
    </div>
  );
};

export default memo(WebPushPermissions);
